import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Loader2 } from "lucide-react";
import {
  diffLines,
  diffWords,
  isMultiline,
  type DiffSegment,
} from "@/lib/diff";

export type DiffViewMode = "diff-only" | "full";

const VIEW_MODE_STORAGE_KEY = "diff-preview-view-mode";
const DIFF_ONLY_CONTEXT_LINES = 2;

function getInitialViewMode(): DiffViewMode {
  if (typeof window === "undefined") return "diff-only";
  try {
    const v = window.sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (v === "diff-only" || v === "full") return v;
  } catch {
    // ignore
  }
  return "diff-only";
}

function collapseUnchanged(
  segments: DiffSegment[],
  contextLines: number,
): { segments: DiffSegment[]; collapsed: { afterIndex: number; count: number }[] } {
  const out: DiffSegment[] = [];
  const collapsed: { afterIndex: number; count: number }[] = [];
  // Group runs of context lines and trim if longer than 2*contextLines + 1
  let i = 0;
  while (i < segments.length) {
    const seg = segments[i];
    if (seg.type !== "context") {
      out.push(seg);
      i++;
      continue;
    }
    // Collect run of context lines
    let j = i;
    while (j < segments.length && segments[j].type === "context") j++;
    const runLen = j - i;
    const atStart = i === 0;
    const atEnd = j === segments.length;
    const head = atStart ? 0 : contextLines;
    const tail = atEnd ? 0 : contextLines;
    if (runLen <= head + tail + 1) {
      for (let k = i; k < j; k++) out.push(segments[k]);
    } else {
      for (let k = i; k < i + head; k++) out.push(segments[k]);
      const hidden = runLen - head - tail;
      collapsed.push({ afterIndex: out.length - 1, count: hidden });
      out.push({
        type: "context",
        text: `… ${hidden} unchanged line${hidden === 1 ? "" : "s"} …`,
      });
      for (let k = j - tail; k < j; k++) out.push(segments[k]);
    }
    i = j;
  }
  return { segments: out, collapsed };
}

function LineDiffView({
  segments,
  collapsedMarkers,
  testId,
}: {
  segments: DiffSegment[];
  collapsedMarkers?: Set<number>;
  testId?: string;
}) {
  return (
    <pre
      className="text-xs font-mono whitespace-pre-wrap leading-relaxed"
      data-testid={testId}
    >
      {segments.map((seg, i) => {
        const isCollapsedMarker = collapsedMarkers?.has(i) ?? false;
        if (isCollapsedMarker) {
          return (
            <div
              key={i}
              className="text-[10px] uppercase tracking-wide text-muted-foreground italic px-2 py-1 border-y border-dashed border-border my-1"
              data-testid={`diff-line-collapsed-${i}`}
            >
              {seg.text}
            </div>
          );
        }
        const bg =
          seg.type === "add"
            ? "bg-green-500/15 text-green-700 dark:text-green-300"
            : seg.type === "remove"
              ? "bg-red-500/15 text-red-700 dark:text-red-300"
              : "text-foreground/80";
        const prefix =
          seg.type === "add" ? "+ " : seg.type === "remove" ? "- " : "  ";
        return (
          <div
            key={i}
            className={`${bg} px-2 py-0.5 rounded-sm`}
            data-testid={`diff-line-${seg.type}-${i}`}
          >
            {prefix}
            {seg.text || "\u200B"}
          </div>
        );
      })}
    </pre>
  );
}

function InlineWordDiffView({
  oldValue,
  newValue,
}: {
  oldValue: string;
  newValue: string;
}) {
  const segments = diffWords(oldValue, newValue);
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
          Before
        </div>
        <div className="text-sm bg-muted/60 rounded p-2 whitespace-pre-wrap">
          {oldValue ? (
            segments
              .filter((s) => s.type !== "add")
              .map((s, i) => (
                <span
                  key={i}
                  className={
                    s.type === "remove"
                      ? "bg-red-500/20 text-red-700 dark:text-red-300 line-through"
                      : ""
                  }
                >
                  {s.text}
                </span>
              ))
          ) : (
            <span className="italic text-muted-foreground">(empty)</span>
          )}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
          After
        </div>
        <div className="text-sm bg-muted/60 rounded p-2 whitespace-pre-wrap">
          {newValue ? (
            segments
              .filter((s) => s.type !== "remove")
              .map((s, i) => (
                <span
                  key={i}
                  className={
                    s.type === "add"
                      ? "bg-green-500/20 text-green-700 dark:text-green-300"
                      : ""
                  }
                >
                  {s.text}
                </span>
              ))
          ) : (
            <span className="italic text-muted-foreground">(empty)</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function DiffPreviewModal({
  open,
  onOpenChange,
  label,
  oldValue,
  newValue,
  onConfirm,
  onCancel,
  isApplying,
  intent = "apply",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  oldValue: string;
  newValue: string;
  onConfirm: () => void;
  onCancel: () => void;
  isApplying?: boolean;
  intent?: "apply" | "review";
}) {
  const multi = isMultiline(oldValue) || isMultiline(newValue);
  const unchanged = oldValue === newValue;

  const [viewMode, setViewMode] = useState<DiffViewMode>(getInitialViewMode);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // ignore
    }
  }, [viewMode]);

  const lineSegments = multi ? diffLines(oldValue, newValue) : [];
  const collapsed =
    multi && viewMode === "diff-only"
      ? collapseUnchanged(lineSegments, DIFF_ONLY_CONTEXT_LINES)
      : null;
  const displayedLineSegments = collapsed ? collapsed.segments : lineSegments;
  const collapsedMarkers = collapsed
    ? new Set(
        collapsed.collapsed.map((c) => c.afterIndex + 1),
      )
    : undefined;

  const title =
    intent === "review" ? `Review change · ${label}` : label;
  const description =
    intent === "review"
      ? "Reviewing this change. Switch view modes to inspect, then confirm to apply or close to keep it as a suggestion."
      : "Review the change before applying. Additions appear in green and removals in red.";
  const confirmLabel = intent === "review" ? "Apply this change" : "Confirm & apply";
  const cancelLabel = intent === "review" ? "Close" : "Cancel";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isApplying) onCancel();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[80vh] flex flex-col"
        data-testid="dialog-diff-preview"
      >
        <DialogHeader>
          <DialogTitle data-testid="text-diff-label">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2">
          <ToggleGroup
            type="single"
            size="sm"
            value={viewMode}
            onValueChange={(v) => {
              if (v === "diff-only" || v === "full") setViewMode(v);
            }}
            data-testid="toggle-diff-view-mode"
          >
            <ToggleGroupItem
              value="diff-only"
              data-testid="toggle-diff-view-mode-diff-only"
            >
              Diff only
            </ToggleGroupItem>
            <ToggleGroupItem
              value="full"
              data-testid="toggle-diff-view-mode-full"
            >
              Full before &amp; after
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <ScrollArea className="flex-1 min-h-0 border border-border rounded-md p-3 bg-background">
          {unchanged ? (
            <div className="text-sm italic text-muted-foreground">
              No changes detected — the proposed value matches the current
              value.
            </div>
          ) : viewMode === "full" ? (
            multi ? (
              <LineDiffView
                segments={lineSegments}
                testId="diff-full-unified"
              />
            ) : (
              <InlineWordDiffView oldValue={oldValue} newValue={newValue} />
            )
          ) : multi ? (
            <LineDiffView
              segments={displayedLineSegments}
              collapsedMarkers={collapsedMarkers}
            />
          ) : (
            <InlineWordDiffView oldValue={oldValue} newValue={newValue} />
          )}
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isApplying}
            data-testid="button-diff-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isApplying || unchanged}
            data-testid="button-diff-confirm"
          >
            {isApplying ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
